import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  CUSTOM_PRACTICE_TIME_OPTIONS,
  CUSTOM_PRACTICE_WHEEL_HOURS,
  CUSTOM_PRACTICE_WHEEL_MINUTES,
  CUSTOM_PRACTICE_WHEEL_PERIODS,
  buildTimeLabelFromParts,
  getSuggestedHoursFromOptions,
  getSuggestedMinutesFromOptions,
  getWheelPeriodsFromOptions,
  getCurrentTimeWheelParts,
  parseTimeLabelToParts,
  resolveWheelTimeSelection,
} from '../../utils/customPractice';

const PANEL_HEIGHT = 280;
const PANEL_MIN_WIDTH = 280;

function getPanelPosition(triggerEl) {
  if (!triggerEl || typeof window === 'undefined') {
    return { top: 0, left: 0, width: PANEL_MIN_WIDTH };
  }
  const rect = triggerEl.getBoundingClientRect();
  const width = Math.max(rect.width, PANEL_MIN_WIDTH);
  let left = rect.left;
  let top = rect.bottom + 6;

  if (left + width > window.innerWidth - 12) {
    left = window.innerWidth - width - 12;
  }
  if (left < 12) {
    left = 12;
  }
  if (top + PANEL_HEIGHT > window.innerHeight - 12) {
    top = rect.top - PANEL_HEIGHT - 6;
  }
  if (top < 12) {
    top = 12;
  }
  return { top, left, width };
}

function TimeColumn({ id, items, value, onSelect, ariaLabel }) {
  const listRef = useRef(null);

  useEffect(() => {
    if (!listRef.current || !value) {
      return;
    }
    const selected = listRef.current.querySelector('[aria-selected="true"]');
    if (selected?.scrollIntoView) {
      selected.scrollIntoView({ block: 'center' });
    }
  }, [value, items]);

  return (
    <div className="cp-time-column" role="presentation">
      <ul
        ref={listRef}
        id={id}
        className="cp-time-column__list"
        role="listbox"
        aria-label={ariaLabel}
      >
        {items.map((item) => (
          <li key={item} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={value === item}
              className={`cp-time-column__item${
                value === item ? ' cp-time-column__item--selected' : ''
              }`}
              onClick={() => onSelect(item)}
            >
              {item}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * MUI-style column time picker (hour | minute | AM/PM + OK / Cancel).
 * `fullWheelLists` shows all 12 hours and 00–59 minutes; invalid start/end pairs
 * are handled by validation in the parent (not by hiding wheel options).
 */
export default function CustomPracticeTimeWheelPicker({
  fieldKey,
  value = '',
  disabled = false,
  placeholder = 'Select time',
  onChange,
  error = false,
  helperText = '',
  timeOptions = null,
  fullWheelLists = false,
}) {
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: PANEL_MIN_WIDTH });
  const [draft, setDraft] = useState(() => parseTimeLabelToParts(value));
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const panelId = `cp-time-panel-${fieldKey}`;

  const allowedOptions = useMemo(() => {
    if (Array.isArray(timeOptions) && timeOptions.length > 0) {
      return timeOptions;
    }
    return CUSTOM_PRACTICE_TIME_OPTIONS;
  }, [timeOptions]);

  const snapOptions =
    Array.isArray(timeOptions) && timeOptions.length > 0
      ? timeOptions
      : fullWheelLists
        ? CUSTOM_PRACTICE_TIME_OPTIONS
        : allowedOptions;

  const hourItems = useMemo(
    () =>
      fullWheelLists
        ? CUSTOM_PRACTICE_WHEEL_HOURS
        : getSuggestedHoursFromOptions(allowedOptions),
    [fullWheelLists, allowedOptions]
  );

  const minuteItems = useMemo(
    () =>
      fullWheelLists
        ? CUSTOM_PRACTICE_WHEEL_MINUTES
        : getSuggestedMinutesFromOptions(
            allowedOptions,
            draft.hour12,
            draft.period
          ),
    [fullWheelLists, allowedOptions, draft.hour12, draft.period]
  );

  const periodItems = useMemo(() => getWheelPeriodsFromOptions(), []);

  useEffect(() => {
    if (!open) {
      setDraft(parseTimeLabelToParts(value));
    }
  }, [value, open]);

  useEffect(() => {
    if (fullWheelLists || !open || !draft.minute) {
      return;
    }
    if (!minuteItems.includes(draft.minute)) {
      setDraft((d) => ({ ...d, minute: minuteItems[0] ?? '' }));
    }
  }, [fullWheelLists, open, minuteItems, draft.minute]);

  useEffect(() => {
    if (fullWheelLists || !open || !draft.hour12) {
      return;
    }
    if (!hourItems.includes(draft.hour12)) {
      setDraft((d) => ({
        ...d,
        hour12: hourItems[0] ?? '',
        minute: '',
      }));
    }
  }, [fullWheelLists, open, hourItems, draft.hour12]);

  const updatePanelPosition = () => {
    if (rootRef.current) {
      setPanelPos(getPanelPosition(rootRef.current));
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    setDraft(parseTimeLabelToParts(value));
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [open, value]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocMouseDown = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const openPanel = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleHourSelect = (hour12) => {
    const nowParts = getCurrentTimeWheelParts();
    setDraft((d) => ({
      ...d,
      hour12,
      minute: nowParts.minute,
      period: nowParts.period,
    }));
  };

  const handleOk = () => {
    const { hour12, minute, period } = draft;
    if (!hour12 || !minute || !period) {
      setOpen(false);
      return;
    }
    const label = fullWheelLists
      ? buildTimeLabelFromParts(hour12, minute, period)
      : resolveWheelTimeSelection(snapOptions, hour12, minute, period) ||
        buildTimeLabelFromParts(hour12, minute, period);
    if (label) {
      onChange({ target: { name: fieldKey, value: label } });
    }
    setOpen(false);
  };

  const panel = open ? (
    <div
      id={panelId}
      ref={panelRef}
      className="cp-time-panel"
      role="dialog"
      aria-label="Select time"
      style={{
        position: 'fixed',
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        zIndex: 10050,
      }}
    >
      <div className="cp-time-panel__columns">
        <TimeColumn
          id={`${fieldKey}-hour-list`}
          items={hourItems}
          value={draft.hour12}
          ariaLabel="Hour"
          onSelect={handleHourSelect}
        />
        <TimeColumn
          id={`${fieldKey}-minute-list`}
          items={minuteItems}
          value={draft.minute}
          ariaLabel="Minute"
          onSelect={(minute) => setDraft((d) => ({ ...d, minute }))}
        />
        <TimeColumn
          id={`${fieldKey}-period-list`}
          items={periodItems}
          value={draft.period}
          ariaLabel="AM or PM"
          onSelect={(period) => setDraft((d) => ({ ...d, period }))}
        />
      </div>
      <div className="cp-time-panel__actions">
        <button
          type="button"
          className="cp-time-panel__btn cp-time-panel__btn--cancel"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="cp-time-panel__btn cp-time-panel__btn--ok"
          onClick={handleOk}
        >
          OK
        </button>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        className={`cp-time-field${open ? ' cp-time-field--open' : ''}${
          error ? ' cp-time-field--error' : ''
        }${disabled ? ' cp-time-field--disabled' : ''}`}
        ref={rootRef}
      >
        <div className="cp-time-field__trigger-wrap">
          <input
            id={`cp-${fieldKey}`}
            type="text"
            readOnly
            className="form-control form-control-solid cp-time-field__input"
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            onClick={openPanel}
            onFocus={openPanel}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
          />
          <button
            type="button"
            className="cp-time-field__icon-btn"
            tabIndex={-1}
            disabled={disabled}
            onClick={openPanel}
            aria-label="Open time picker"
          >
            <i className="fas fa-clock" aria-hidden="true" />
          </button>
        </div>
        {helperText ? (
          <p className="cp-time-field__hint text-danger mb-0">{helperText}</p>
        ) : null}
      </div>
      {typeof document !== 'undefined' && panel
        ? ReactDOM.createPortal(panel, document.body)
        : null}
    </>
  );
}
